import { Component } from '@theme/component';

/**
 * Flash Timer Web Component
 * Refreshes every 30 minutes for demo purposes.
 * 
 * @typedef {Object} FlashTimerRefs
 * @property {HTMLElement} timer - The timer display element
 * 
 * @extends {Component<FlashTimerRefs>}
 */
class FlashTimer extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#startTimer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) {
      clearInterval(this.#interval);
    }
  }

  #startTimer() {
    this.#update();
    this.#interval = setInterval(() => this.#update(), 1000);
  }

  #update() {
    if (!this.refs.timer) return;

    const endDateAttr = this.dataset.bonusEndDate;
    if (!endDateAttr) {
      this.refs.timer.textContent = 'ENTRIES PER $1 SPENT';
      return;
    }

    const endDate = new Date(endDateAttr).getTime();
    if (isNaN(endDate)) {
      this.refs.timer.textContent = 'ENTRIES PER $1 SPENT';
      return;
    }

    const now = new Date().getTime();
    const distance = endDate - now;

    if (distance < 0) {
      this.refs.timer.textContent = 'ENTRIES PER $1 SPENT';
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    const dayText = days === 1 ? 'day' : 'days';
    const hourText = hours === 1 ? 'hour' : 'hours';

    this.refs.timer.textContent = `Ending in ${days} ${dayText} and ${hours} ${hourText}`;
  }

  /** @type {number|null} */
  #interval = null;
}

if (!customElements.get('flash-timer')) {
  customElements.define('flash-timer', FlashTimer);
}

/**
 * Giveaway Countdown Web Component
 * Counts down to a specified date.
 * 
 * @typedef {Object} GiveawayCountdownRefs
 * @property {HTMLElement} timer - The timer display element
 * 
 * @extends {Component<GiveawayCountdownRefs>}
 */
class GiveawayCountdown extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    const endDateAttr = this.dataset.endDate;
    // Fallback to a default if not provided
    this.#endDate = endDateAttr ? new Date(endDateAttr).getTime() : new Date("2026-12-31T23:59:59").getTime();
    
    this.#startTimer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) {
      clearInterval(this.#interval);
    }
  }

  #startTimer() {
    this.#update();
    this.#interval = setInterval(() => this.#update(), 1000);
  }

  #update() {
    if (!this.refs.timer || !this.#endDate) return;

    const now = new Date().getTime();
    const distance = this.#endDate - now;
    const countdownBox = this.closest('.countdown-box-container');
    const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

    if (distance < 0) {
      this.refs.timer.textContent = "00:00:00:00";
      if (countdownBox) countdownBox.style.display = 'none';
      clearInterval(this.#interval);
      return;
    }

    if (distance > ONE_WEEK_MS) {
      if (countdownBox) countdownBox.style.display = 'none';
      return;
    } else {
      if (countdownBox) countdownBox.style.display = 'block';
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    const dDisplay = days > 0 ? `${days}D:` : "";
    this.refs.timer.textContent = `${dDisplay}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /** @type {number|null} */
  #endDate = null;
  /** @type {number|null} */
  #interval = null;
}

if (!customElements.get('giveaway-countdown')) {
  customElements.define('giveaway-countdown', GiveawayCountdown);
}
